import type { Media } from "@elektr/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera, Trash2 } from "lucide-react";
import { useRef, useState } from "react";

import { Spinner } from "@/components/ui/card";
import { useToast } from "@/components/ui/toast";
import { api, errorMessage, unwrap } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { uploadFile } from "@/lib/upload";

const IN_FLIGHT = ["pending", "uploaded", "processing"];

/** Shchit rasmlari: ro'yxat, kameradan/galereyadan yuklash, o'chirish. */
export function PanelPhotos({ propertyId, panelId }: { propertyId: string; panelId: string }) {
  const { t } = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<{ name: string; pct: number } | null>(null);
  const key = ["panel-media", propertyId, panelId];

  const photos = useQuery({
    queryKey: key,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/media/", {
          params: { query: { property: propertyId, panel: panelId, page_size: 50 } },
        }),
      ),
    // Qayta ishlanayotgan rasm tayyor bo'lguncha vaqti-vaqti bilan so'raymiz
    refetchInterval: (q) =>
      q.state.data?.results.some((m) => IN_FLIGHT.includes(m.status ?? "")) ? 3000 : false,
  });

  async function onFiles(files: FileList | null) {
    if (!files?.length) return;
    for (const file of Array.from(files)) {
      setBusy({ name: file.name, pct: 0 });
      try {
        await uploadFile(
          api,
          file,
          { property: propertyId, panel: panelId, kind: "photo" },
          { onProgress: (loaded) => setBusy({ name: file.name, pct: loaded / (file.size || 1) }) },
        );
        void qc.invalidateQueries({ queryKey: key });
      } catch (e) {
        toast.error(errorMessage(e, t("common.error")));
      }
    }
    setBusy(null);
    if (input.current) input.current.value = "";
  }

  const remove = useMutation({
    mutationFn: async (m: Media) => {
      const res = await api.DELETE("/api/media/{id}/", { params: { path: { id: m.id } } });
      if (!res.response.ok) throw new Error("delete");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });

  const items = photos.data?.results ?? [];
  return (
    <div className="mb-3">
      <div className="mb-2 text-sm font-semibold">{t("panels.photos")}</div>
      <div className="flex flex-wrap gap-2">
        {items.map((m) => (
          <div
            key={m.id}
            className="group relative size-24 overflow-hidden rounded-lg border border-border bg-muted"
          >
            {m.thumb_url ? (
              <a href={m.preview_url ?? m.thumb_url} target="_blank" rel="noopener noreferrer">
                <img src={m.thumb_url} alt="" className="size-full object-cover" loading="lazy" />
              </a>
            ) : (
              <div className="flex size-full items-center justify-center">
                <Spinner className="size-5" />
              </div>
            )}
            <button
              type="button"
              aria-label={t("common.delete")}
              onClick={() => remove.mutate(m)}
              className="absolute right-1 top-1 rounded-full bg-black/60 p-1.5 text-white"
            >
              <Trash2 className="size-3.5" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => input.current?.click()}
          disabled={!!busy}
          className="flex size-24 flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-border text-xs text-muted-foreground transition-colors hover:border-primary hover:text-primary disabled:opacity-60"
        >
          {busy ? (
            <>
              <Spinner className="size-5" />
              <span>{Math.round(busy.pct * 100)}%</span>
            </>
          ) : (
            <>
              <Camera className="size-6" />
              <span>{t("panels.add_photo")}</span>
            </>
          )}
        </button>
      </div>
      <input
        ref={input}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => void onFiles(e.target.files)}
        data-testid="panel-photo-input"
      />
    </div>
  );
}
